import React, { useState } from 'react';
import GetRequest from '../Services/APIRequest';
import Loading from '../Components/Loading';
import Error from '../Components/Error';

const formatDateDDMMM = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short' };
    return date.toLocaleDateString('en-GB', options).replace(' ', '-');
};

const getStatusClass = (status) => {
    return "orderStatus_" + status.replace(/\s+/g, '');
};

const AccordionRow = ({ item, level }) => {
    const [isOpen, setIsOpen] = useState(false);
    const hasChildren = item.items?.length > 0;

    // Calculate totals for parent order
    const totalOrdered = item.items ? item.items.reduce((sum, c) => sum + (c.orderedQty || 0), 0) : 0;
    const totalDispatched = item.items ? item.items.reduce((sum, c) => sum + (c.dispatchedQty || 0), 0) : 0;
    const totalBalance = totalOrdered - totalDispatched >= 0 ? totalOrdered - totalDispatched : 0;

    return (
        <>
            <tr className={getStatusClass(item.status)}>
                <td className="text-center align-middle">
                    {hasChildren && (
                        <button
                            onClick={() => setIsOpen(!isOpen)}
                            className="btn btn-sm"
                            title={isOpen ? 'Collapse' : 'Expand'}
                            aria-expanded={isOpen}
                            aria-controls={`order-children-${item.orderNumber}`}
                        >
                            <i className={`fa fa-chevron-${isOpen ? 'down' : 'right'}`} />
                        </button>
                    )}
                </td>
                <td style={{ paddingLeft: `${level * 20}px` }}><b>{item.orderNumber}</b></td>

                {/* Change these three from text-end to text-center */}
                <td className="text-center fw-bold">{totalOrdered}</td>
                <td className="text-center fw-bold">{totalDispatched}</td>
                <td className="text-center fw-bold">{totalBalance}</td>

                <td className="text-end fw-bold">{item.status}</td>
            </tr>

            {isOpen && hasChildren && item.items.map((child, idx) => {
                const balance = (child.orderedQty || 0) - (child.dispatchedQty || 0);
                return (
                    <tr
                        key={idx}
                        className={getStatusClass(child.dispatchStatus)}
                        id={`order-children-${item.orderNumber}`}
                    >
                        <td></td>
                        <td style={{ paddingLeft: `${(level + 1) * 20}px` }}>{child.itemName}</td>
                        {/* Change these three from text-end to text-center */}
                        <td className="text-center">{child.orderedQty}</td>
                        <td className="text-center">{child.dispatchedQty}</td>
                        <td className="text-center">{balance >= 0 ? balance : 0}</td>
                        <td className="text-end">
                            <span className={`status-badge status-${child.dispatchStatus.replace(/\s/g, '')}`}>
                                {child.dispatchStatus}
                            </span>
                        </td>
                    </tr>
                );
            })}

            {/* Child item rows */}
            {isOpen && hasChildren && item.items.map((child, idx) => {
                const balance = (child.orderedQty || 0) - (child.dispatchedQty || 0);
                return (
                    <tr
                        key={idx}
                        className={getStatusClass(child.dispatchStatus)}
                        id={`order-children-${item.orderNumber}`}
                    >
                        <td></td>
                        <td style={{ paddingLeft: `${(level + 1) * 20}px` }}>{child.itemName}</td>
                        <td className="text-center">{child.orderedQty}</td>
                        <td className="text-center">{child.dispatchedQty}</td>
                        <td className="text-center">{balance >= 0 ? balance : 0}</td>
                        <td className="text-end">
                            <span className={`status-badge status-${child.dispatchStatus.replace(/\s/g, '')}`}>
                                {child.dispatchStatus}
                            </span>
                        </td>
                    </tr>
                );
            })}
        </>
    );
};

// 🔹 Date Range Helpers
const getDateRange = (filter) => {
    const now = new Date();
    let start, end = new Date();

    switch (filter) {
        case 'Today':
            start = new Date(now.setHours(0, 0, 0, 0));
            end = new Date(now.setHours(23, 59, 59, 999));
            break;
        case 'This Week':
            start = new Date(now);
            start.setDate(start.getDate() - start.getDay());
            start.setHours(0, 0, 0, 0);
            end = new Date();
            end.setHours(23, 59, 59, 999);
            break;
        case 'Last Week':
            start = new Date(now);
            start.setDate(start.getDate() - start.getDay() - 7);
            start.setHours(0, 0, 0, 0);
            end = new Date(start);
            end.setDate(end.getDate() + 6);
            end.setHours(23, 59, 59, 999);
            break;
        case 'Last 15 Days':
            start = new Date();
            start.setDate(now.getDate() - 14);
            start.setHours(0, 0, 0, 0);
            end = new Date();
            end.setHours(23, 59, 59, 999);
            break;
        case 'This Month':
        default:
            start = new Date();
            start.setDate(now.getDate() - 30);
            start.setHours(0, 0, 0, 0);
            end = new Date();
            end.setHours(23, 59, 59, 999);
            break;
    }

    return { startDate: start, endDate: end };
};

const OrderDispatchTreeView = () => {
    const distID = localStorage.getItem('distributorId');
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    const apiStartDate = `${yyyy}-${mm}-01`;
    const apiEndDate = `${yyyy}-${mm}-${dd}`;

    const { data, error, isLoaded } = GetRequest(
        `/Orders/GetOrderDispatchReportTreeFormat/singleistributor/${apiStartDate}/${apiEndDate}/${distID}`
    );

    const [statusFilter, setStatusFilter] = useState('All');
    const [dateFilter, setDateFilter] = useState('This Month');

    if (error) return <Error data="Order Dispatch Report" />;
    if (!isLoaded) return <Loading />;

    const orders = data.result || [];

    const allStatuses = new Set(['All']);
    orders.forEach(order => {
        allStatuses.add(order.status);
        (order.items || []).forEach(child => allStatuses.add(child.dispatchStatus));
    });

    const { startDate: dateStart, endDate: dateEnd } = getDateRange(dateFilter);
    const filteredByDate = orders.filter(order => {
        const orderDate = new Date(order.orderPlacedDate);
        return orderDate >= dateStart && orderDate <= dateEnd;
    });

    const filteredOrders = statusFilter === 'All'
        ? filteredByDate
        : filteredByDate.filter(order =>
            order.status === statusFilter ||
            (order.items || []).some(child => child.dispatchStatus === statusFilter)
        );

    const dateFilters = ['Today', 'This Week', 'Last Week', 'Last 15 Days', 'This Month'];

    return (
        <section className="content">
            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            {/* 🔹 Status Filter Buttons */}
                            <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                                <h5 className="card-title mb-0">Order Dispatch Summary</h5>
                            </div>

                            {/* 🔹 Date Filter Buttons */}
                            <div className="card-body pb-0">
                                <div>
                                    {[...allStatuses].map(status => (
                                        <button
                                            key={status}
                                            className={`btn btn-sm me-2 mb-1 ${statusFilter === status ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setStatusFilter(status)}
                                        >
                                            {status}
                                        </button>
                                    ))}
                                </div>
                                <div className="mb-3">
                                    {dateFilters.map(label => (
                                        <button
                                            key={label}
                                            className={`btn btn-sm me-2 mb-2 ${dateFilter === label ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => {
                                                setDateFilter(label);
                                                setStatusFilter('All'); // Reset status filter on date change
                                            }}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* 🔹 Orders Table */}
                            <div className="card-body pt-0">
                                <table className="table table-bordered table-hover table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="text-center" style={{ width: '40px' }}></th>
                                            <th>Order / Item</th>
                                            <th className="text-center">Ordered</th>
                                            <th className="text-center">Supplied</th>
                                            <th className="text-center">Balance</th>
                                            <th className="text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="text-center text-muted">No orders found for the selected filters.</td>
                                            </tr>
                                        ) : (
                                            filteredOrders.map((order, index) => (
                                                <AccordionRow key={index} item={order} level={0} />
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>

                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default OrderDispatchTreeView;
